import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Award, Users, Wrench, MessageCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import useSEO from '../hooks/useSEO';

const About = () => {
  const { t } = useLanguage();
  useSEO({
    title: 'Επικοινωνία - Τηλέφωνο, WhatsApp, Viber | PrimeTech Santorini',
    description: 'Επικοινωνήστε με την PrimeTech Santorini: τηλέφωνο, WhatsApp & Viber +30 6987350644, email primetechsantorini@gmail.com. Ωράριο 08:00-16:00, επείγοντα 24/7.',
  });

  const phoneNumber = '+30 6987350644';
  const whatsappNumber = '306987350644'; // Without + and spaces for WhatsApp link
  const email = 'primetechsantorini@gmail.com';

  const contactMethods = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: { el: 'Τηλέφωνο', en: 'Phone' },
      content: { el: phoneNumber, en: phoneNumber },
      link: `tel:${whatsappNumber}`,
      color: 'bg-blue-100 text-blue-600',
      buttonColor: 'bg-blue-500 hover:bg-blue-600',
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: { el: 'WhatsApp', en: 'WhatsApp' },
      content: { el: phoneNumber, en: phoneNumber },
      link: `https://wa.me/${whatsappNumber}`,
      color: 'bg-green-100 text-green-600',
      buttonColor: 'bg-green-500 hover:bg-green-600',
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: { el: 'Viber', en: 'Viber' },
      content: { el: phoneNumber, en: phoneNumber },
      link: `viber://chat?number=%2B${whatsappNumber}`,
      color: 'bg-purple-100 text-purple-600',
      buttonColor: 'bg-purple-500 hover:bg-purple-600',
      badge: { el: 'ΓΡΗΓΟΡΗ ΑΠΑΝΤΗΣΗ', en: 'QUICK REPLY' },
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: { el: 'Email', en: 'Email' },
      content: { el: email, en: email },
      link: `mailto:${email}`,
      color: 'bg-orange-100 text-orange-600',
      buttonColor: 'bg-orange-500 hover:bg-orange-600',
    },
  ];

  const workingHours = [
    {
      days: { el: 'Δευτέρα - Σάββατο', en: 'Monday - Saturday' },
      hours: '08:00 - 16:00',
    },
    {
      days: { el: 'Κυριακή', en: 'Sunday' },
      hours: { el: 'ΚΛΕΙΣΤΑ', en: 'CLOSED' },
      closed: true,
    },
  ];

  const values = [
    {
      icon: <Award className="w-12 h-12" />,
      title: { el: 'Επαγγελματισμός', en: 'Professionalism' },
      description: {
        el: 'Πιστοποιημένοι τεχνικοί με χρόνια εμπειρίας',
        en: 'Certified technicians with years of experience'
      }
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: { el: 'Αξιοπιστία', en: 'Reliability' },
      description: {
        el: 'Διαθέσιμοι όταν μας χρειάζεστε',
        en: 'Available when you need us'
      }
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      title: { el: 'Ποιότητα', en: 'Quality' },
      description: {
        el: 'Γνήσια ανταλλακτικά και επαγγελματικός εξοπλισμός',
        en: 'Genuine parts and professional equipment'
      }
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section 
        className="relative py-24 bg-cover bg-center" 
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1661602715735-d401dafd3615)' }}
        data-testid="about-hero"
      >
        <div className="absolute inset-0 bg-slate-900/70"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
            {t({ el: 'ΕΠΙΚΟΙΝΩΝΙΑ', en: 'CONTACT' })}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            {t({
              el: 'Εδρεύουμε στη Σαντορίνη και εξυπηρετούμε όλη τη Σαντορίνη',
              en: 'Based in Santorini, serving all of Santorini'
            })}
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">
              {t({ el: 'ΕΠΙΚΟΙΝΩΝΗΣΤΕ ΜΑΖΙ ΜΑΣ', en: 'GET IN TOUCH' })}
            </h2>
            <p className="text-lg text-slate-600">
              {t({
                el: 'Επιλέξτε τον τρόπο που σας εξυπηρετεί καλύτερα',
                en: 'Choose the method that suits you best'
              })}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {contactMethods.map((method, index) => (
              <div
                key={index}
                data-testid={`contact-method-${index}`}
                className="bg-slate-50 border-2 border-slate-200 p-8 card-hover relative"
              >
                {method.badge && (
                  <div className="absolute top-4 right-4 bg-orange-500 text-white text-xs font-bold px-3 py-1 tracking-wider">
                    {t(method.badge)}
                  </div>
                )}
                
                <div className={`w-14 h-14 ${method.color} rounded-full flex items-center justify-center mb-4`}>
                  {method.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {t(method.title)}
                </h3>
                <p className="text-slate-700 mb-6 font-medium technical-font">
                  {t(method.content)}
                </p>
                <a
                  href={method.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-btn-${index}`}
                  className={`${method.buttonColor} text-white px-6 py-3 font-bold tracking-wider transition-colors inline-flex items-center space-x-2`}
                >
                  {method.icon}
                  <span>{t({ el: 'ΕΠΙΚΟΙΝΩΝΙΑ', en: 'CONTACT' })}</span>
                </a>
              </div>
            ))}
          </div>

          {/* Working Hours */}
          <div className="bg-slate-50 border-2 border-slate-200 p-10 mb-16">
            <div className="flex items-center justify-center mb-8">
              <Clock className="w-12 h-12 text-orange-500 mr-4" />
              <h2 className="text-3xl font-extrabold text-slate-900">
                {t({ el: 'ΩΡΑΡΙΟ ΛΕΙΤΟΥΡΓΙΑΣ', en: 'WORKING HOURS' })}
              </h2>
            </div>
            
            <div className="max-w-md mx-auto space-y-4">
              {workingHours.map((schedule, index) => (
                <div
                  key={index}
                  data-testid={`working-hours-${index}`}
                  className={`flex justify-between items-center p-4 ${
                    schedule.closed ? 'bg-red-50 border border-red-200' : 'bg-white border border-slate-200'
                  }`}
                >
                  <span className="font-semibold text-slate-700">
                    {t(schedule.days)}
                  </span>
                  <span className={`font-bold technical-font ${
                    schedule.closed ? 'text-red-600' : 'text-orange-600'
                  }`}>
                    {typeof schedule.hours === 'object' ? t(schedule.hours) : schedule.hours}
                  </span>
                </div>
              ))}
            </div>

            <div className="text-center mt-8 p-4 bg-orange-50 border border-orange-200">
              <p className="text-orange-800 font-semibold">
                {t({
                  el: '⚡ Για ΕΠΕΙΓΟΝΤΑ περιστατικά χρησιμοποιήστε WhatsApp 24/7',
                  en: '⚡ For URGENT cases use WhatsApp 24/7'
                })}
              </p>
            </div>
          </div>

          {/* About Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-6">
                {t({ el: 'ΣΧΕΤΙΚΑ ΜΕ ΕΜΑΣ', en: 'ABOUT US' })}
              </h2>
              <div className="space-y-4 text-lg text-slate-700">
                <p>
                  {t({
                    el: 'Η PrimeTech Santorini είναι μια εξειδικευμένη τεχνική εταιρεία που παρέχει ολοκληρωμένες λύσεις για επαγγελματικές κουζίνες στη Σαντορίνη.',
                    en: 'PrimeTech Santorini is a specialized technical company providing complete solutions for commercial kitchens in Santorini.'
                  })}
                </p>
                <p>
                  {t({
                    el: 'Ασχολούμαστε με επισκευές ηλεκτρικού εξοπλισμού και εγκαταστάσεις/συντήρηση συστημάτων εξαερισμού για επαγγελματικές κουζίνες.',
                    en: 'We handle electric equipment repairs and ventilation system installation and maintenance for commercial kitchens.'
                  })}
                </p>
                <p>
                  {t({
                    el: 'Εξυπηρετούμε εστιατόρια, ξενοδοχεία και catering επιχειρήσεις με επαγγελματισμό και αξιοπιστία.',
                    en: 'We serve restaurants, hotels and catering businesses with professionalism and reliability.'
                  })}
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_e6cf0493-e8d0-4f07-a179-d2f65d4a4d25/artifacts/i0f9befd_IMG_0902.png"
                alt="Επαγγελματική κουζίνα - σχέδιο"
                className="w-full h-[500px] object-cover shadow-2xl"
              />
            </div>
          </div>

          {/* Values */}
          <div className="mb-24">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-12 text-center">
              {t({ el: 'ΟΙ ΑΞΙΕΣ ΜΑΣ', en: 'OUR VALUES' })}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  data-testid={`value-${index}`}
                  className="bg-slate-50 border border-slate-200 p-10 text-center card-hover"
                >
                  <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 text-orange-600">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {t(value.title)}
                  </h3>
                  <p className="text-slate-600">
                    {t(value.description)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Service Areas */}
          <div className="bg-slate-50 border border-slate-200 p-12 text-center">
            <MapPin className="w-16 h-16 text-orange-500 mx-auto mb-6" />
            <h2 className="text-3xl font-extrabold text-slate-900 mb-4">
              {t({ el: 'ΠΕΡΙΟΧΕΣ ΕΞΥΠΗΡΕΤΗΣΗΣ', en: 'SERVICE AREAS' })}
            </h2>
            <p className="text-lg text-slate-700 mb-6">
              {t({
                el: 'Εξυπηρετούμε όλη τη Σαντορίνη',
                en: 'Serving all of Santorini'
              })}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {['Σαντορίνη'].map((island, index) => (
                <span
                  key={index}
                  className="bg-white px-6 py-3 text-slate-700 font-semibold border border-slate-200"
                >
                  {island}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 py-24" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
            {t({ el: 'ΕΤΟΙΜΟΙ ΝΑ ΣΑΣ ΒΟΗΘΗΣΟΥΜΕ', en: 'READY TO HELP YOU' })}
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            {t({
              el: 'Επικοινωνήστε μαζί μας άμεσα για οποιαδήποτε τεχνική υποστήριξη',
              en: 'Contact us immediately for any technical support'
            })}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/chat"
              data-testid="about-chat-btn"
              className="bg-orange-500 text-white px-12 py-5 text-lg font-bold tracking-wider hover:bg-orange-600 transition-colors inline-flex items-center justify-center space-x-2"
            >
              <Phone className="w-6 h-6" />
              <span>{t({ el: 'AI CHAT', en: 'AI CHAT' })}</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;